import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Checkbox,
  Drawer,
  IconButton,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  ListItemText,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ClearIcon from '@mui/icons-material/Clear'

const MobileCompositeFilterDropdown = ({ groupedFilter, onChange, selectedFilters }) => {
  const theme = useTheme()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectOpen, setSelectOpen] = useState(false)

  // Filter the grouped filters based on selectedFilters
  const selectedGroupedFilters = groupedFilter.filters.filter(
    filter => filter.filterDefinition.name && selectedFilters[filter.filterDefinition.name],
  )

  const toggleDrawer = open => () => {
    setIsDrawerOpen(open)
  }

  const handleRemoveFilter = (e, filter) => {
    e.stopPropagation()
    onChange({
      filterDefinition: filter.filterDefinition,
      filterGroupName: groupedFilter.displayName,
      selectedValues: [],
    })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        onClick={toggleDrawer(true)}
        variant='outlined'
        size='small'
        sx={{
          width: '100%',
          textTransform: 'none',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: '4px',
          borderColor: theme.palette.divider,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {groupedFilter.displayName}{' '}
        {selectedGroupedFilters.length > 0 ? `(${selectedGroupedFilters.length})` : ''}
      </Button>

      <Drawer
        anchor='bottom'
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: { 
            padding: 2, 
            maxHeight: '90vh', 
            overflowY: 'auto',
            zIndex: 1400,
          },
        }}
        sx={{
          zIndex: 1400,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            cursor: 'pointer',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2,
            paddingY: 1,
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px',
            paddingX: 2,
          }}
        >
          <Box>
            <Typography variant='h6' sx={{ color: theme.palette.text.primary }}>
              {groupedFilter.displayName}
            </Typography>
            <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
              Selections apply automatically
            </Typography>
          </Box>
          <IconButton 
            onClick={toggleDrawer(false)}
            sx={{
              width: '36px',
              height: '36px',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <FormControl fullWidth>
          <InputLabel
            sx={{
              fontSize: '.9rem',
              color: theme.palette.text.primary,
              marginBottom: '12px', // Ensures proper spacing below the label
              '&.MuiInputLabel-shrink': {
                color: theme.palette.primary.main,
              },
            }}
            id={`label-${groupedFilter.filterGroup}`}
          >
            {groupedFilter.displayName}
          </InputLabel>
          <Select
            labelId={`label-${groupedFilter.filterGroup}`}
            value={selectedGroupedFilters.map(filter => filter.filterDefinition.name)}
            multiple
            open={selectOpen}
            onOpen={() => setSelectOpen(true)}
            onClose={() => setSelectOpen(false)}
            renderValue={() => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedGroupedFilters.map(filter => (
                  <Chip
                    key={filter.filterDefinition.name}
                    label={filter.filterDefinition.displayName}
                    onDelete={e => handleRemoveFilter(e, filter)}
                    sx={{
                      fontSize: '.7rem',
                      backgroundColor: theme.palette.grey[200],
                      color: theme.palette.text.primary,
                      height: '24px',
                      maxWidth: '200px', // Prevent chips from getting too wide on mobile
                      '& .MuiChip-label': {
                        fontSize: '.7rem',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '150px', // Limit label width on mobile
                      },
                      '& .MuiChip-deleteIcon': {
                        fontSize: '18px', // Make delete icon more visible on mobile
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.error.main,
                        },
                      },
                    }}
                    deleteIcon={<ClearIcon sx={{ fontSize: '18px' }} />}
                    onMouseDown={e => e.stopPropagation()} // Prevent dropdown from opening
                  />
                ))}
              </Box>
            )}
            MenuProps={{
              sx: { zIndex: 1500 },
            }}
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              '& .MuiSelect-select': {
                padding: '12px 16px', // Increase padding for better appearance
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
              },
            }}
          >
            {groupedFilter.filters.map(filter => {
              const isChecked = (
                selectedFilters[filter.filterDefinition.name]?.selectedValues || []
              ).some(f => f.value === '1')

              return (
                <MenuItem
                key={filter.filterDefinition.name}
                value={filter.filterDefinition.name}
                onClick={() => {
                  const selectedValues = isChecked ? [] : [{ value: '1', name: 'Yes' }]
                  onChange({
                    filterDefinition: filter.filterDefinition,
                    filterGroupName: groupedFilter.displayName,
                    selectedValues,
                  })
                }}
              >
                  <Checkbox checked={isChecked} />
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: '.8rem',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {filter.filterDefinition.displayName}
                      </Typography>
                    }
                  />
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
      </Drawer>
    </Box>
  )
}

export default MobileCompositeFilterDropdown